import { useI18n } from '@/hooks/useI18n';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { Button } from '@/components/ui/button';
import { ArrowRight, MessageSquare, Shield, Zap, Globe, Sparkles, Play, Send, User, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';

export const HomePage = () => {
  const { t } = useI18n();
  useDocumentLang();

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-50 selection:bg-blue-100 selection:text-blue-900">
      {/* Animated Background Blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full mix-blend-multiply filter blur-[80px] opacity-60 animate-float-slow bg-[var(--color-pastel-cyan)]"></div>
        <div className="absolute top-[10%] -right-[10%] w-[60vw] h-[60vw] rounded-full mix-blend-multiply filter blur-[80px] opacity-60 animate-float-slower bg-[var(--color-pastel-pink)] delay-1000"></div>
        <div className="absolute -bottom-[20%] left-[20%] w-[60vw] h-[60vw] rounded-full mix-blend-multiply filter blur-[80px] opacity-60 animate-float-medium bg-[var(--color-pastel-lavender)] delay-2000"></div>
        <div className="absolute bottom-[10%] -right-[10%] w-[50vw] h-[50vw] rounded-full mix-blend-multiply filter blur-[80px] opacity-50 animate-float-fast bg-[var(--color-pastel-mint)] delay-3000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      {/* Navbar Placeholder (Assuming there's a layout, but if not, we add spacing) */}
      <div className="relative z-10">
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-4">
          <div className="container mx-auto text-center">
            
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-white/60 shadow-sm mb-8 animate-fade-in-up">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
              </span>
              <span className="text-sm font-medium text-slate-600">{t('home.hero.badge')}</span>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-slate-900 mb-8 max-w-5xl mx-auto leading-[1.1]">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900">
                {t('home.hero.title')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              {t('home.hero.subtitle')}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20">
              {/* TODO: Re-enable Try Demo link later
              <Link to="/demo">
                <Button size="lg" className="h-14 px-8 rounded-full text-lg bg-slate-900 hover:bg-slate-800 text-white shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  {t('home.hero.tryDemo')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              */}
              <Link to="/contact">
                <Button size="lg" variant="outline" className="h-14 px-8 rounded-full text-lg bg-white/80 border-slate-200 hover:bg-white hover:border-slate-300 text-slate-700 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm">
                  <Play className="mr-2 h-5 w-5 fill-slate-700" />
                  {t('home.hero.contactSales')}
                </Button>
              </Link>
            </div>

            {/* Visual Element / Screenshot Placeholder */}
            <div className="relative max-w-5xl mx-auto mt-8 md:mt-16 p-2 md:p-4 rounded-3xl bg-gradient-to-b from-white/40 to-white/10 backdrop-blur-2xl border border-white/40 shadow-2xl ring-1 ring-black/5 transform hover:scale-[1.01] transition-transform duration-700">
               <div className="rounded-2xl overflow-hidden min-h-[500px] md:min-h-0 md:aspect-[21/9] bg-slate-50 relative group shadow-inner flex flex-col md:flex-row">
                   
                   {/* Sidebar (Restaurant Info) - Hidden on small screens */}
                   <div className="hidden md:flex w-1/3 bg-slate-900 p-8 flex-col justify-between relative overflow-hidden">
                       <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center" />
                       <div className="relative z-10">
                           <div className="h-12 w-12 rounded-full bg-orange-500 flex items-center justify-center mb-6 shadow-lg shadow-orange-500/20">
                              <span className="text-2xl">🍝</span>
                           </div>
                           <h3 className="text-2xl font-bold text-white mb-2">Trattoria Al Dente</h3>
                           <p className="text-slate-400 text-sm leading-relaxed">Authentic Italian dining experience with AI-powered reservations.</p>
                       </div>
                       <div className="relative z-10 space-y-4">
                           <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                               <div className="text-xs text-slate-400 mb-1">Status</div>
                               <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                                   <span className="relative flex h-2 w-2">
                                     <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                     <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                   </span>
                                   Concierge Online
                               </div>
                           </div>
                       </div>
                   </div>

                   {/* Chat Interface */}
                   <div className="flex-1 bg-white flex flex-col relative">
                       {/* Header (Mobile only) */}
                       <div className="md:hidden p-4 border-b border-slate-100 flex items-center gap-3 bg-white sticky top-0 z-10">
                            <div className="h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center text-sm shadow-md">🍝</div>
                            <div className="font-bold text-slate-900">Trattoria Al Dente</div>
                            <div className="ml-auto flex items-center gap-2 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full border border-green-100">
                                <span className="relative flex h-1.5 w-1.5">
                                     <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                     <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                                </span>
                                Online
                            </div>
                       </div>

                       {/* Messages Area */}
                       <div className="flex-1 p-4 md:p-8 overflow-y-auto flex flex-col gap-4 md:gap-6 bg-slate-50/50 scroll-smooth">
                           
                           {/* Message 1: AI Intro */}
                           <div className="flex gap-3 md:gap-4 animate-fade-in-up" style={{ animationFillMode: 'both' }}>
                               <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                                   <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                               </div>
                               <div className="flex flex-col gap-1 max-w-[85%] md:max-w-[80%]">
                                   <span className="text-[10px] md:text-xs font-medium text-slate-400 ml-1">AI Concierge</span>
                                   <div className="p-3 md:p-4 rounded-2xl rounded-tl-none bg-white border border-slate-100 shadow-sm text-slate-600 text-sm md:text-base leading-relaxed">
                                       Buonasera! Welcome to <span className="font-semibold text-slate-800">Trattoria Al Dente</span>. How can I assist you with your reservation tonight?
                                   </div>
                               </div>
                           </div>

                           {/* Message 2: User Request */}
                           <div className="flex gap-3 md:gap-4 flex-row-reverse animate-fade-in-up" style={{ animationDelay: '1s', animationFillMode: 'both' }}>
                               <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                                   <User className="h-4 w-4 md:h-5 md:w-5 text-slate-500" />
                               </div>
                               <div className="flex flex-col gap-1 items-end max-w-[85%] md:max-w-[80%]">
                                   <div className="p-3 md:p-4 rounded-2xl rounded-tr-none bg-blue-600 text-white shadow-md text-sm md:text-base leading-relaxed">
                                       Hi! Do you have a table for 2 people tonight at 7 PM?
                                   </div>
                               </div>
                           </div>

                           {/* Message 3: AI Response */}
                           <div className="flex gap-3 md:gap-4 animate-fade-in-up" style={{ animationDelay: '2.5s', animationFillMode: 'both' }}>
                               <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                                   <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                               </div>
                               <div className="flex flex-col gap-1 max-w-[85%] md:max-w-[80%]">
                                   <span className="text-[10px] md:text-xs font-medium text-slate-400 ml-1">AI Concierge</span>
                                    <div className="p-3 md:p-4 rounded-2xl rounded-tl-none bg-white border border-slate-100 shadow-sm text-slate-600 text-sm md:text-base leading-relaxed">
                                       Let me check availability for you... <br/><br/>
                                       Yes! I have a lovely table by the window available at <span className="font-semibold text-blue-600">7:00 PM</span>. Would you like me to book it for you?
                                   </div>
                               </div>
                           </div>
                       </div>

                       {/* Input Area */}
                       <div className="p-3 md:p-6 bg-white border-t border-slate-100">
                           <div className="relative">
                               <input 
                                   type="text" 
                                   disabled
                                   placeholder="Type your message..." 
                                   className="w-full h-10 md:h-14 pl-4 md:pl-6 pr-12 md:pr-14 rounded-full bg-slate-50 border-slate-200 text-slate-400 text-sm md:text-base focus:outline-none focus:ring-0 cursor-not-allowed"
                                   defaultValue="Yes, please book it!"
                               />
                               <div className="absolute right-1 top-1 bottom-1 md:right-2 md:top-2 md:bottom-2 aspect-square bg-blue-600 rounded-full flex items-center justify-center shadow-md opacity-50">
                                   <Send className="h-4 w-4 md:h-5 md:w-5 text-white" />
                               </div>
                           </div>
                       </div>
                   </div>
               </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-32 px-4">
          <div className="container mx-auto max-w-7xl">
             <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
                  {t('home.features.title')}
                </h2>
                <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                  {t('home.features.subtitle')}
                </p>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                <FeatureCard 
                   icon={<MessageSquare className="h-6 w-6" />}
                   title={t('home.features.ai.title')}
                   description={t('home.features.ai.description')}
                   color="bg-blue-500"
                />
                <FeatureCard 
                   icon={<Globe className="h-6 w-6" />}
                   title={t('home.features.multilingual.title')}
                   description={t('home.features.multilingual.description')}
                   color="bg-indigo-500"
                />
                <FeatureCard 
                   icon={<Zap className="h-6 w-6" />}
                   title={t('home.features.realtime.title')}
                   description={t('home.features.realtime.description')}
                   color="bg-amber-500"
                />
                <FeatureCard 
                   icon={<Shield className="h-6 w-6" />}
                   title={t('home.features.secure.title')}
                   description={t('home.features.secure.description')}
                   color="bg-emerald-500"
                />
             </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-32 px-4 overflow-hidden">
          <div className="container mx-auto max-w-5xl relative z-10">
            <div className="bg-slate-900 rounded-[2.5rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl">
               {/* CTA Background Effects */}
               <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                   <div className="absolute -top-[50%] -left-[20%] w-[80%] h-[80%] bg-blue-600/30 rounded-full blur-3xl animate-float-slow"></div>
                   <div className="absolute bottom-0 right-0 w-[60%] h-[60%] bg-indigo-600/30 rounded-full blur-3xl animate-float-medium"></div>
               </div>

               <div className="relative z-10">
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    {t('home.cta.title')}
                  </h2>
                  <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                    {t('home.cta.subtitle')}
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="h-14 px-10 rounded-full text-lg bg-white text-slate-900 hover:bg-blue-50 hover:scale-105 transition-all duration-300 shadow-lg border-none">
                      {t('home.cta.button')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
               </div>
            </div>
          </div>
        </section>

        {/* Footer with version */}
        <footer className="relative py-8 text-center">
          <p className="text-xs text-slate-400">v{import.meta.env.VITE_APP_VERSION}</p>
        </footer>
      </div>
    </div>
  );
};

type FeatureCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
};

const FeatureCard = ({ icon, title, description, color }: FeatureCardProps) => (
  <div className="group p-8 rounded-3xl bg-white/40 backdrop-blur-md border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
    <div className={`w-14 h-14 ${color} bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
      <div className={`${color.replace('bg-', 'text-')}`}>{icon}</div>
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{description}</p>
  </div>
);